import { useState } from "react";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import { formatDate } from "../../utils/converter";
import AILogsModal from "./AILogsModal";

const UsageDetails = ({ usageData }) => {
    const { content } = useContentfulContext();
    const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
    const filteredUsageData = usageData?.filter(item => item.feature !== "get_global_templates") || [];

    const formatFeatureName = (name) => {
        if (!name) return "";
        return name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }

    /**
     * Detects the JS max-date sentinel (new Date(8640000000000000) = year 275760).
     * This is written by the backend when interval_in_days === -1 on a feature,
     * meaning the usage counter NEVER resets (e.g. invite_user = lifetime limit).
     */
    const isNeverExpires = (dateStr) => {
        if (!dateStr) return false;
        return new Date(dateStr).getFullYear() > 9999;
    }

    /**
     * Returns a human-readable renew date string:
     *   - "Never"  → feature has no reset cycle (interval_in_days = -1 / renew_date is null)
     *   - formatted date string otherwise
     */
    const formatRenewDate = (dateStr) => {
        if (!dateStr) return "Never";
        if (isNeverExpires(dateStr)) return "Never";
        return formatDate(dateStr);
    }

    if (!filteredUsageData || filteredUsageData.length === 0) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-500">{content?.OWNER_PLAN?.no_usage}</p>
            </div>
        )
    }

    // Separate ai_credits from regular feature usage
    const aiCreditsItem = filteredUsageData.find(item => item.feature === "ai_credits")
    const featureItems = filteredUsageData.filter(item => item.feature !== "ai_credits")

    const renderProgressBar = (used, max) => {
        const isUnlimited = max === -1
        const usagePercentage = isUnlimited ? 0 : ((used / max) * 100 || 0)

        if (isUnlimited) {
            return (
                <div className="flex items-center">
                    <div className="h-2 w-full bg-gray-200 rounded-full">
                        <div className="h-2 bg-primary rounded-full w-1/4"></div>
                    </div>
                    <span className="ml-2 text-xs text-gray-500">Unlimited</span>
                </div>
            )
        }

        return (
            <div className="space-y-1">
                <div className="h-2 w-full bg-gray-200 rounded-full">
                    <div
                        className={`h-2 rounded-full ${
                            usagePercentage > 90
                                ? "bg-red-500"
                                : usagePercentage > 70
                                    ? "bg-yellow-500"
                                    : "bg-green-500"
                        }`}
                        style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                    ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                    <span>{Math.round(usagePercentage)}% used</span>
                    <span>{Math.max(0, max - used)} remaining</span>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* AI Credits Section */}
            {aiCreditsItem && (aiCreditsItem.max_allowed_usage === -1 || aiCreditsItem.max_allowed_usage > 0 || (aiCreditsItem.topup_credits || 0) > 0) && (
                <div className="mb-2">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">AI Credits</h3>
                        <button
                            type="button"
                            onClick={() => setIsLogsModalOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-white hover:bg-indigo-50 border border-indigo-200 rounded-lg shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                        >
                            <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                            View Logs
                        </button>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-4 shadow-sm border border-indigo-100">
                        <div className="flex flex-col space-y-3">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        <h3 className="text-lg font-semibold text-gray-800">AI Credits</h3>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Renews on: {formatRenewDate(aiCreditsItem.renew_date)}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <span className="text-2xl font-bold text-gray-800">
                                        {aiCreditsItem.current_usage || 0}
                                        <span className="text-gray-500 text-lg">
                                            /{aiCreditsItem.max_allowed_usage === -1 ? "∞" : (aiCreditsItem.max_allowed_usage + (aiCreditsItem.topup_credits || 0))}
                                        </span>
                                    </span>
                                    {aiCreditsItem.topup_credits > 0 && (
                                        <p className="text-xs text-indigo-500 font-medium">Includes {aiCreditsItem.topup_credits} Top-up</p>
                                    )}
                                    <p className="text-xs text-gray-500">Used</p>
                                </div>
                            </div>
                            {renderProgressBar(aiCreditsItem.current_usage || 0, aiCreditsItem.max_allowed_usage === -1 ? -1 : (aiCreditsItem.max_allowed_usage + (aiCreditsItem.topup_credits || 0)))}
                        </div>
                    </div>
                </div>
            )}

            {/* Feature Usage Section */}
            {featureItems.length > 0 && (
                <div>
                    {aiCreditsItem && (
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Feature Usage</h3>
                    )}
                    <div className="grid grid-cols-1 gap-4">
                        {featureItems.map((item, index) => {
                            const isUnlimited = item.max_allowed_usage === -1
                            const maxAllowed = isUnlimited ? -1 : (item.max_allowed_usage + (item.topup_credits || 0))
                            const renewDate = formatRenewDate(item.renew_date)

                            return (
                                <div key={index} className="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-100">
                                    <div className="flex flex-col space-y-3">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-800">{item?.feature_name || formatFeatureName(item?.feature)}</h3>
                                                <p className="text-sm text-gray-500">Renews on: {renewDate}</p>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-2xl font-bold text-gray-800">
                                                    {item.current_usage}
                                                    <span className="text-gray-500 text-lg">/{isUnlimited ? "∞" : maxAllowed}</span>
                                                </span>
                                                <p className="text-xs text-gray-500">Used</p>
                                            </div>
                                        </div>

                                        <div className="w-full">
                                            {renderProgressBar(item.current_usage, maxAllowed)}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            <div className="flex justify-end mt-4">
                <div className="flex items-center space-x-4 text-sm">
                    <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-1"></div>
                        <span>Good</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full mr-1) "></div>
                        <span>Warning</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-3 h-3 bg-red-500 rounded-full mr-1"></div>
                        <span>Critical</span>
                    </div>
                </div>
            </div>

            <AILogsModal
                isOpen={isLogsModalOpen}
                onClose={() => setIsLogsModalOpen(false)}
            />
        </div>
    )
}

export default UsageDetails
